import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Maybe } from '@codegrade/maybe';
import { NonEmptyList } from '@codegrade/non-empty-list';
/** A component to group some UI elements into a visually-distinct context. */
export const Card = tsx.component({
    name: 'cg-card',
    props: {
        /* Tags */
        /** Tag of the card element. */
        tag: p(String).default('section'),
        /** Tag of the title text. */
        titleTag: p.ofStringLiterals('span', 'h2', 'h3', 'h4', 'h5', 'h6').default('h3'),
        /** Short-hand to add a header with just text to the card. */
        header: p(String).optional,
        /** Optional classes for header. */
        headerClass: p.ofType().optional,
        /** Optional classes for body. */
        bodyClass: p.ofType().optional,
        /** Optional classes for footer. */
        footerClass: p.ofType().optional,
        /* Appearance */
        /** Size of the card. */
        size: p.ofStringLiterals('default', 'small').default('default'),
        /** Variant of the card. */
        variant: p.ofStringLiterals('neutral', 'actionable', 'critical').default('neutral'),
    },
    methods: {
        /**
         * Takes the header slot and render it in a wrapper.
         *
         * It will first check if the header prop is passed, if not it will fallback to the header slot.
         *
         * When using both the header prop and the header slot, the slot content will be placed next to the header text.
         * This is useful when using a DescriptionPopover, it will be placed on the end side of the header, balancing the card.
         *
         * Needs to be a method because slots are not reactive, if it is a computed prop it won't auto-update itself.
         *
         * @returns Maybe a header for the card.
         */
        maybeHeader() {
            const maybeHeaderText = Maybe.fromNullable(this.header);
            const maybeHeaderSlot = Maybe.fromNullable(this.$slots.header);
            const hasBoth = maybeHeaderText.isJust() && maybeHeaderSlot.isJust();
            const children = NonEmptyList.fromArray(Maybe.catMaybes([
                maybeHeaderText.map((str) => this.$createElement(this.titleTag, { class: 'cg-card-header-title' }, str)),
                hasBoth ? Maybe.of(<div class="grow"/>) : Maybe.empty(),
                maybeHeaderSlot,
            ]));
            return children.map(c => (<header class={['cg-card-header', this.headerClass]}>{c}</header>));
        },
        /**
         * Takes the default slot and render it in a wrapper.
         *
         * Needs to be a method because slots are not reactive, if it is a computed prop it won't auto-update itself.
         *
         * @returns Maybe a body for the card.
         */
        maybeBody() {
            return Maybe.fromNullable(this.$slots.default).map(children => (<div class={['cg-card-body', this.bodyClass]}>
                    {children}
                    <div class="clear-both"/>
                    {/* Add a clear-both empty div here because so many of our right-aligned buttons are using float: right to layout... */}
                </div>));
        },
        /**
         * Takes the footer slot and render it in a wrapper.
         *
         * Needs to be a method because slots are not reactive, if it is a computed prop it won't auto-update itself.
         *
         * @returns Maybe a footer for the card.
         */
        maybeFooter() {
            return Maybe.fromNullable(this.$slots.footer).map(children => (<footer class={['cg-card-footer', this.footerClass]}>{children}</footer>));
        },
    },
    /** @returns Node of the component. */
    render() {
        return this.$createElement(this.tag, {
            class: [
                'cg-card',
                `cg-card-${this.variant}`,
                {
                    'cg-card-small': this.size === 'small',
                },
            ],
        }, Maybe.fromNullable(this.$slots.raw).orDefault(Maybe.catMaybes([this.maybeHeader(), this.maybeBody(), this.maybeFooter()])));
    },
});
